import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Mousewheel } from "swiper";
import type {Swiper as SwiperType} from "swiper";

import "swiper/css";
import styled from "styled-components";
import { MutableRefObject, useEffect, useRef, useState } from "react";

const SlideShowPaddedContainer = styled.div<{ hasYPadding?: boolean }>`
  height: 100%;

  ${(props) =>
    props.hasYPadding
      ? `${props.theme.apply("default", (breakpoint: string) => {
          return `
        padding-top: ${props.theme.spacePx(breakpoint, 7)};
        padding-bottom: ${props.theme.spacePx(breakpoint, 9)};
        `;
        })}`
      : ""}
`;

const SlideshowContainer = styled.div`
  height: 100%;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        & .swiper-slide,
        & .swiper-backface-hidden .swiper-slide {
          backface-visibility: hidden;
          transform: translateX(${props.theme.marginPx(
            breakpoint
          )}) translateZ(0);
        }
        `;
    })}

  & .swiper {
    height: 100%;
    width: 100%;
  }
  & .swiper-slide {
    height: 100%;
    width: auto;
    user-select: none;
    box-sizing: border-box;
  }

  & .swiper-wrapper {
    height: 100%;
    display: flex;
  }
`;

const Slide = styled.div`
  height: 100%;
  user-select: none;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
      &.last {
        padding-right: ${(2 * props.theme.margin(breakpoint)).toFixed(2)}px;
      }
      `;
    })}
`;

const SwiperContainer = styled.div`
  width: 100%;
  height: 100%;

  & .swiper-button-prev {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 50%;
    height: 100%;
    pointer-events: none;
    z-index: 2;

    cursor: url("/img/arrows/IKON_Left_64px.png") 38 32, pointer;
    cursor: -webkit-image-set(
          url("/img/arrows/IKON_Left_64px.png") 1x,
          url("/img/arrows/IKON_Left_128px.png") 2x
        )
        38 32,
      pointer;

    &.swiper-button-disabled {
      cursor: url("/img/arrows/IKON_Left_Inactive_64px.png"), pointer;
      cursor: -webkit-image-set(
            url("/img/arrows/IKON_Left_Inactive_64px.png") 1x,
            url("/img/arrows/IKON_Left_Inactive_128px.png") 2x
          )
          38 32,
        pointer;
    }
  }

  & .swiper-button-next {
    position: absolute;
    top: 0px;
    left: 50%;
    width: 50%;
    height: 100%;
    pointer-events: none;
    z-index: 2;
    cursor: url("/img/arrows/IKON_Right_64px.png"), pointer;
    cursor: -webkit-image-set(
          url("/img/arrows/IKON_Right_64px.png") 1x,
          url("/img/arrows/IKON_Right_128px.png") 2x
        )
        38 32,
      pointer;

    &.swiper-button-disabled {
      cursor: url("/img/arrows/IKON_Right_Inactive_64px.png"), pointer;
      cursor: -webkit-image-set(
            url("/img/arrows/IKON_Right_Inactive_64px.png") 1x,
            url("/img/arrows/IKON_Right_Inactive_128px.png") 2x
          )
          38 32,
        pointer;
    }
  }
  @media (any-hover: none) {
    & .swiper-button-prev,
    & .swiper-button-next {
      display: none;
    }
  }
`;

export const HeroSlideShow = ({
  slides,
  hasYPadding,
}: {
  slides: React.ReactNode[];
  hasYPadding?: boolean;
}) => {
  const prevRef = useRef() as MutableRefObject<HTMLDivElement>;
  const nextRef = useRef() as MutableRefObject<HTMLDivElement>;
  const [swiperKey, setSwiperKey] = useState("hero-swiper");

  useEffect(() => {
    if (typeof window === "undefined" || !slides?.length) return;

    setSwiperKey("hero-swiper-rebuild");
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!Array.isArray(slides) || slides.length === 0) return <></>;

  return (
    <SlideShowPaddedContainer hasYPadding={hasYPadding}>
      <SlideshowContainer>
        <SwiperContainer
          onPointerEnter={(event) => {
            if (event.pointerType === "mouse") {
              prevRef.current.style.pointerEvents = "all";
              nextRef.current.style.pointerEvents = "all";
            }
          }}
          onPointerLeave={(event) => {
            if (event.pointerType === "mouse") {
              prevRef.current.style.pointerEvents = "none";
              nextRef.current.style.pointerEvents = "none";
            }
          }}
          onTouchStartCapture={() => {
            prevRef.current.style.pointerEvents = "none";
            nextRef.current.style.pointerEvents = "none";
          }}
        >
          <Swiper
            key={swiperKey}
            // onInit={() => {
            // }}
            navigation={{
              prevEl: ".swiper-button-prev",
              nextEl: ".swiper-button-next",
            }}
            modules={[Navigation, Mousewheel]}
            spaceBetween={0}
            grabCursor={true}
            edgeSwipeDetection
            mousewheel={{
              forceToAxis: true,
              sensitivity: 1,
              thresholdDelta: 60,
            }}
            slidesPerView="auto"
            slidesOffsetBefore={0}
          >
            {slides.map((slide, index: number) => (
              <SwiperSlide key={`swiper-${index}`}>
                <Slide
                  key={`slideshow-slide-${index}`}
                  className={
                    index === 0
                      ? "first"
                      : index === slides.length - 1
                      ? "last"
                      : ""
                  }
                >
                  {slide}
                </Slide>
              </SwiperSlide>
            ))}
            <div ref={prevRef} className="swiper-button-prev"></div>
            <div ref={nextRef} className="swiper-button-next"></div>
          </Swiper>
        </SwiperContainer>
      </SlideshowContainer>
    </SlideShowPaddedContainer>
  );
};

export default HeroSlideShow;
